import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { GALLERY } from "../data/content";

export const Gallery = () => {
  const [index, setIndex] = useState(null);
  const close = () => setIndex(null);
  const step = useCallback((d) => setIndex((i) => (i + d + GALLERY.length) % GALLERY.length), []);

  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, step]);

  return (
    <section id="galleria" className="bg-cream py-24 md:py-36" data-testid="gallery-section">
      <div className="container-x">
        <SectionHeading
          id="gallery"
          eyebrow="Galleria"
          title={<>Ambienti, dettagli e <em className="font-normal italic text-sage">risultati</em>.</>}
          subtitle="Uno sguardo dentro Biovital: le cabine, i prodotti che usiamo ogni giorno e i piccoli gesti che rendono ogni trattamento un momento solo tuo."
        />
        <div className="mt-16 grid auto-rows-[260px] grid-cols-2 gap-4 md:auto-rows-[320px] md:grid-cols-3">
          {GALLERY.map((g, i) => (
            <Reveal key={g.src} delay={(i % 4) * 0.08} className={`${g.span} h-full`}>
              <button
                onClick={() => setIndex(i)}
                className="group relative block h-full w-full overflow-hidden rounded-2xl"
                aria-label={`Apri ${g.alt}`}
                data-testid={`gallery-item-${i}`}
              >
                <img src={g.src} alt={g.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110" />
                <span className="absolute inset-0 bg-sage-dark/0 transition-colors duration-500 group-hover:bg-sage-dark/40" />
                <span className="absolute bottom-4 left-4 flex translate-y-3 items-center gap-2 text-xs font-medium text-cream opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <Maximize2 className="h-3.5 w-3.5" /> {g.alt}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {index !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-sm"
            onClick={close}
            data-testid="lightbox"
            data-lenis-prevent
          >
            <button onClick={close} className="absolute right-5 top-5 rounded-full border border-cream/20 p-2.5 text-cream hover:bg-cream/10" aria-label="Chiudi" data-testid="lightbox-close">
              <X className="h-5 w-5" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); step(-1); }} className="absolute left-3 md:left-8 rounded-full border border-cream/20 p-3 text-cream hover:bg-cream/10" aria-label="Precedente" data-testid="lightbox-prev">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <motion.img
              key={GALLERY[index].src}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.4 }}
              src={GALLERY[index].src}
              alt={GALLERY[index].alt}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[90vw] rounded-2xl object-contain shadow-2xl"
              data-testid="lightbox-image"
            />
            <button onClick={(e) => { e.stopPropagation(); step(1); }} className="absolute right-3 md:right-8 rounded-full border border-cream/20 p-3 text-cream hover:bg-cream/10" aria-label="Successiva" data-testid="lightbox-next">
              <ChevronRight className="h-5 w-5" />
            </button>
            <span className="absolute bottom-6 text-xs uppercase tracking-[0.25em] text-cream/70">
              {index + 1} / {GALLERY.length} — {GALLERY[index].alt}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
